import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import type { DesignBuilding } from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import { parseDesignConfig } from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import { historyContext } from 'src/modules/designStudio/providers';
import { useDesignConfig } from 'src/modules/designStudio/providers/BuildingWrapperProvider/useDesignConfig';
import {
  createBuildingAppearances,
  createBuildingMutations,
} from 'src/modules/designStudio/providers/MacheteBuildingProvider/helpers';
import type {
  BuildingAppearances,
  MutateBuildingFunction,
} from 'src/modules/designStudio/providers/MacheteBuildingProvider/types';

export const useBuildingWrapper = ({
  building,
}: {
  building: DesignBuilding | undefined;
}): {
  buildingAppearances: BuildingAppearances;
  mutateBuilding: (update: MutateBuildingFunction) => void;
} => {
  const [initialTemplateConfigApplied, setInitialTemplateConfigApplied] =
    useState(false);
  const {
    historyState,
    updateDesignConfigFromBuilding: notifyHistoryMachineOfChange,
  } = useContext(historyContext);

  const { designConfig, updateDesignConfig } = useDesignConfig({ building });

  const buildingAppearances = useMemo(
    () => createBuildingAppearances(building),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [building, designConfig],
  );

  const mutateBuilding = useCallback(
    async (buildingFunction: MutateBuildingFunction) => {
      if (building) {
        const mutations = createBuildingMutations(building);
        await buildingFunction(mutations, buildingAppearances);
        notifyHistoryMachineOfChange();
        updateDesignConfig();
      }
    },
    [
      building,
      notifyHistoryMachineOfChange,
      buildingAppearances,
      updateDesignConfig,
    ],
  );

  /**
   * This function is called once, at load time when we have fetched the last saved design config from firebase.
   * The method setHistory() can't be relied upon to convert the file format if needed.
   * This function does two things:
   *
   *  1. It converts the file format if needed.
   *  2. It applies the file format to the building if using template config system.
   */
  const { history } = historyState;
  const loadThroughTemplateConfigSystem = () => {
    void (async () => {
      if (building && !initialTemplateConfigApplied && history?.length) {
        const latestVersion = history[history.length - 1];
        setInitialTemplateConfigApplied(true);
        const config = parseDesignConfig(latestVersion.design_config);
        /**
         * Note: We cann't invoke mutateBuilding to apply the loaded design config because
         * it invokes updateDesignConfigFromBuilding. updateDesignConfigFromBuilding is meant to
         * only be called when the user has made a change and causes the undo button to become clickable.
         * If we applied the loaded change using mutateBuilding the undo button would be active at load time.
         */
        const mutations = createBuildingMutations(building);
        await mutations.applyDesignConfig(config);
        updateDesignConfig();
      }
    })();
  };
  useEffect(loadThroughTemplateConfigSystem, [
    history,
    building,
    initialTemplateConfigApplied,
    setInitialTemplateConfigApplied,
    updateDesignConfig,
  ]);

  return {
    buildingAppearances,
    mutateBuilding,
  };
};
